import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';

/**
 * Overview's building block (Part B: "cards for Open inbounds, Orders to
 * pick, ..."). Each card fetches its own count independently so one slow
 * or failing category never blocks the rest of the page (mirrors the
 * server's own listProblems() union-not-join reasoning). A permission
 * error renders nothing - Overview only shows a card at all when the
 * caller's role can see the underlying data (courtesy hiding to match Nav,
 * Part C).
 */
export function StatCard({
  label,
  href,
  fetchCount,
}: {
  label: string;
  href: string;
  fetchCount: () => Promise<number>;
}): JSX.Element | null {
  const { locale } = useLanguage();
  const [state, setState] = useState<{ status: 'loading' | 'ok' | 'hidden' | 'error'; value?: number }>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchCount().then(
      (value) => {
        if (!cancelled) setState({ status: 'ok', value });
      },
      (err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) setState({ status: 'hidden' });
        else setState({ status: 'error' });
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.status === 'hidden') return null;

  return (
    <Link to={href} className={`stat-card ${state.value === 0 ? 'n-zero' : ''}`}>
      <div className="n">{state.status === 'ok' ? formatNumber(state.value!, locale) : state.status === 'error' ? '—' : '…'}</div>
      <div className="label">{label}</div>
    </Link>
  );
}
